// /src/pages/Overview.js
import React, { Component } from "react";
import { calculateOrderStats } from "../services/orderStatsApi";

import Notify from "../utils/Notify";
import StatsCard from "./components/stats-cards/StatsCard";
import OverviewTable from "./components/tables/OverViewTable";
import Sales from "./Sales";

import FetchHelper from "../utils/FetchHelper";

/* ===== ICON COLORS ===== */
const ICON_COLOR = "#F64E60"; // original soft pink

/* ===== HELPERS ===== */
function normalizeCounty(value = "") {
  return String(value).trim().toLowerCase();
}

function toTitleCase(s = "") {
  if (!s) return "";
  return s
    .split(" ")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Extract county/city from multiple possible order shapes.
 * IMPORTANT: Adjust this if your API has a known single field.
 */
function extractCounty(order) {
  const raw =
    order?.data?.customer_address?.county ||
    order?.data?.customer_address?.city ||
    order?.data?.customer_address?.town ||
    order?.venue?.company?.town ||
    order?.venue?.company?.title ||
    order?.customer_address?.county ||
    order?.customer_address?.city ||
    order?.county ||
    order?.city ||
    "";

  const v = normalizeCounty(raw);

  // normalize common cases
  if (v.includes("cork")) return "Cork";
  if (v.includes("limerick")) return "Limerick";

  return toTitleCase(v);
}

/**
 * ✅ Exclude ONLY from "Current Orders" card:
 * If delivery is pending AND status is rejected => not current.
 *
 * We read from multiple shapes because API may differ.
 * Update the keys here if you know the exact field names.
 */
function isRejectedAndDeliveryPending(order) {
  const status = String(
    order?.status ||
      order?.data?.status ||
      order?.order_status ||
      order?.data?.order_status ||
      ""
  )
    .trim()
    .toLowerCase();

  const deliveryStatus = String(
    order?.delivery_status ||
      order?.data?.delivery_status ||
      order?.driver_status ||
      order?.data?.driver_status ||
      ""
  )
    .trim()
    .toLowerCase();

  const isRejected =
    status === "rejected" || status.includes("rejected") || status.includes("reject");

  const isDeliveryPending =
    deliveryStatus === "pending" || deliveryStatus.includes("pending");

  return isRejected && isDeliveryPending;
}

/* ===== ICONS ===== */

// Vendor / Store Icon (Accepted)
const vendorIcon = (
  <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
    <rect x={0} y={0} width={24} height={24} />
    <path
      d="M4 9 L20 9 L18.5 5.5 C18.2 4.9 17.6 4.5 16.9 4.5 L7.1 4.5 C6.4 4.5 5.8 4.9 5.5 5.5 L4 9 Z"
      fill={ICON_COLOR}
      opacity="1"
    />
    <rect
      x={4}
      y={9}
      width={16}
      height={9}
      rx={1.5}
      fill={ICON_COLOR}
      opacity="0.85"
    />
    <rect x={9.5} y={11} width={3} height={5} rx={0.6} fill="#FFFFFF" />
  </g>
);

// Vendor Icon (Pending lighter)
const vendorIconPending = (
  <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd" opacity="0.85">
    <rect x={0} y={0} width={24} height={24} />
    <path
      d="M4 9 L20 9 L18.5 5.5 C18.2 4.9 17.6 4.5 16.9 4.5 L7.1 4.5 C6.4 4.5 5.8 4.9 5.5 5.5 L4 9 Z"
      fill={ICON_COLOR}
      opacity="0.85"
    />
    <rect x={4} y={9} width={16} height={9} rx={1.5} fill={ICON_COLOR} opacity="0.45" />
  </g>
);

// Delivery Truck (Driver Accepted)
const driverIcon = (
  <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
    <rect x={0} y={0} width={24} height={24} />
    <path
      d="M3 10 L14 10 C14.5523 10 15 10.4477 15 11 L15 16 C15 16.5523 14.5523 17 14 17 L5 17 C4.4477 17 4 16.5523 4 16 L3 10 Z"
      fill={ICON_COLOR}
      opacity="1"
    />
    <path
      d="M15 11 L18.5 11 C18.8 11 19.1 11.1 19.3 11.3 L21 13.2 C21.3 13.5 21.5 13.9 21.5 14.3 L21.5 16
         C21.5 16.5523 21.0523 17 20.5 17 L15 17 L15 11 Z"
      fill={ICON_COLOR}
      opacity="0.8"
    />
    <circle cx={7} cy={18.5} r={1.5} fill={ICON_COLOR} />
    <circle cx={17.5} cy={18.5} r={1.5} fill={ICON_COLOR} />
  </g>
);

// Orders / Clipboard Icon (Current Orders)
const ordersIcon = (
  <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
    <rect x={0} y={0} width={24} height={24} />
    <path
      d="M7 5.5C7 4.67 7.67 4 8.5 4H15.5C16.33 4 17 4.67 17 5.5V19
         C17 20.1 16.1 21 15 21H9
         C7.9 21 7 20.1 7 19V5.5Z"
      fill={ICON_COLOR}
      opacity="0.22"
    />
    <path
      d="M9 4.8C9 3.81 9.81 3 10.8 3H13.2C14.19 3 15 3.81 15 4.8V6
         H9V4.8Z"
      fill={ICON_COLOR}
      opacity="0.9"
    />
    <rect x={9} y={9} width={6.5} height={1.4} rx={0.7} fill={ICON_COLOR} opacity="0.9" />
    <rect x={9} y={12} width={7.5} height={1.4} rx={0.7} fill={ICON_COLOR} opacity="0.75" />
    <rect x={9} y={15} width={5.5} height={1.4} rx={0.7} fill={ICON_COLOR} opacity="0.6" />
  </g>
);

// Rejected Icon (X / cancel)
const rejectedIcon = (
  <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
    <rect x={0} y={0} width={24} height={24} />
    <circle cx={12} cy={12} r={9} fill={ICON_COLOR} opacity="0.18" />
    <path d="M9.2 9.2 L14.8 14.8" stroke={ICON_COLOR} strokeWidth="2" strokeLinecap="round" />
    <path d="M14.8 9.2 L9.2 14.8" stroke={ICON_COLOR} strokeWidth="2" strokeLinecap="round" />
  </g>
);

// Driver Pending (lighter truck)
const driverIconPending = (
  <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd" opacity="0.85">
    <rect x={0} y={0} width={24} height={24} />
    <path
      d="M3 10 L14 10 C14.5523 10 15 10.4477 15 11 L15 16 C15 16.5523 14.5523 17 14 17 L5 17 C4.4477 17 4 16.5523 4 16 L3 10 Z"
      fill={ICON_COLOR}
      opacity="0.85"
    />
    <path
      d="M15 11 L18.5 11 C18.8 11 19.1 11.1 19.3 11.3 L21 13.2 C21.3 13.5 21.5 13.9 21.5 14.3 L21.5 16
        C21.5 16.5523 21.0523 17 20.5 17 L15 17 L15 11 Z"
      fill={ICON_COLOR}
      opacity="0.3"
    />
    <circle cx={7} cy={18.5} r={1.5} fill={ICON_COLOR} opacity="0.5" />
    <circle cx={17.5} cy={18.5} r={1.5} fill={ICON_COLOR} opacity="0.5" />
  </g>
);




function isPaymentFailed(order) {
  const orderStatus = String(
    order?.status ||
      order?.data?.status ||
      order?.order_status ||
      order?.data?.order_status ||
      ""
  )
    .trim()
    .toLowerCase();

  const paymentStatus = String(
    order?.payment?.status ||
      order?.payment?.payment_status ||
      order?.payment?.statusLabel ||
      order?.data?.payment?.status ||
      order?.data?.payment?.payment_status ||
      order?.payment_status ||
      order?.data?.payment_status ||
      ""
  )
    .trim()
    .toLowerCase();

  return (
    orderStatus === "payment_failed" ||
    orderStatus === "payment failed" ||
    paymentStatus === "payment_failed" ||
    paymentStatus === "payment failed" ||
    paymentStatus === "failed"
  );
}



/* ===== COMPONENT ===== */
export default class Overview extends Component {
  constructor(props) {
    super(props);

  //   this.state = {
  //     stats: null,
  //     isLoadingStats: false,
  //     showSales: false,

  //     // ✅ County filter
  //     selectedCounty: "All",
  //     counties: ["All"],
  //     allOrders: [],
  //   };
  // }





  this.state = {
  stats: {
    current_orders: { total: 0, cork: 0, limerick: 0 },
    vendor_accepted: { total: 0, cork: 0, limerick: 0 },
    vendor_pending: { total: 0, cork: 0, limerick: 0 },
    driver_accepted: { total: 0, cork: 0, limerick: 0 },
    driver_pending: { total: 0, cork: 0, limerick: 0 },
    rejected: { total: 0, cork: 0, limerick: 0 },
  },
  isLoadingStats: false,
  selectedCounty: "All",
  counties: ["All"],
  allOrders: [],
};

  }


  componentDidMount() {
  this.loadAllOrdersForStats();
}

buildOrdersUrl = ({
  page = 1,
  pageSize = 100,
} = {}) => {
  const separator = window.Api.Orders.includes("?") ? "&" : "?";

  return (
    `${window.Api.Orders}${separator}` +
    `page=${page}` +
    `&page_size=${pageSize}` +
    `&pagination_type=page` +
    `&ordering=-created_at`
  );
};

loadAllOrdersForStats = async () => {
  this.setState({ isLoadingStats: true });

  try {
    const separator = window.Api.Orders.includes("?") ? "&" : "?";

    let nextUrl =
      `${window.Api.Orders}${separator}` +
      "page=1&page_size=100&pagination_type=page";

    const allOrders = [];
    const visitedUrls = new Set();

    while (nextUrl && !visitedUrls.has(nextUrl)) {
      visitedUrls.add(nextUrl);

      const response = await FetchHelper.get(nextUrl, false);

      const pageOrders = Array.isArray(response)
        ? response
        : Array.isArray(response?.results)
        ? response.results
        : [];

      allOrders.push(...pageOrders);

      nextUrl = Array.isArray(response)
        ? null
        : response?.next || null;
    }

    const ordersById = new Map();

    allOrders.forEach((order, index) => {
      const key =
        order?.id != null
          ? String(order.id)
          : `order-${index}`;

      ordersById.set(key, order);
    });

    const uniqueOrders = Array.from(ordersById.values());

    const countySet = new Set();

    uniqueOrders.forEach((order) => {
      const county = extractCounty(order);

      if (county) {
        countySet.add(county);
      }
    });

    this.setState(
      {
        allOrders: uniqueOrders,
        counties: [
          "All",
          ...Array.from(countySet).sort(),
        ],
        isLoadingStats: false,
      },

      () => this.recomputeStats()
    );
  } catch (error) {
    console.error("Failed to load orders for overview:", error);
    Notify.error(error.message || "Failed to load order statistics");

    this.setState({
      isLoadingStats: false,
    });
  }
};

 


  toggleSales = () => {
    this.setState((prev) => ({ showSales: !prev.showSales }));
  };

  handleCountyChange = (e) => {
    const selectedCounty = e.target.value;
    this.setState({ selectedCounty }, () => {
      this.recomputeStats();
    });
  };


  


  recomputeStats = () => {
  const { allOrders, selectedCounty } = this.state;

  try {
    const filteredOrders =
      selectedCounty === "All"
        ? allOrders
        : allOrders.filter(
            (order) =>
              extractCounty(order) === selectedCounty
          );

    // Calculate the other cards using every filtered order.
    const calculatedStats =
      calculateOrderStats(filteredOrders) || {};

    // Exclude payment_failed and rejected/pending orders
    // only from the Current Orders card.
    const currentOrders = filteredOrders.filter(
      (order) =>
        !isRejectedAndDeliveryPending(order) &&
        !isPaymentFailed(order)
    );

    const currentOrderStats =
      calculateOrderStats(currentOrders) || {};

    const emptyStats = {
      total: 0,
      cork: 0,
      limerick: 0,
    };

    this.setState({
      stats: {
        current_orders:
          currentOrderStats.current_orders || emptyStats,

        vendor_accepted:
          calculatedStats.vendor_accepted || emptyStats,

        vendor_pending:
          calculatedStats.vendor_pending || emptyStats,

        driver_accepted:
          calculatedStats.driver_accepted || emptyStats,

        driver_pending:
          calculatedStats.driver_pending || emptyStats,

        rejected:
          calculatedStats.rejected || emptyStats,
      },
    });

    console.log("Overview statistics calculated:", {
      allOrders: allOrders.length,
      filteredOrders: filteredOrders.length,
      currentOrders: currentOrders.length,
      calculatedStats,
      currentOrderStats,
    });
  } catch (error) {
    console.error("Failed to calculate overview stats:", error);
    Notify.error("Failed to calculate overview statistics");
  }
};






handleOrdersLoaded = (orders) => {
  try {
    const list = Array.isArray(orders) ? orders : [];

    const countySet = new Set();

    list.forEach((order) => {
      const county = extractCounty(order);

      if (county) {
        countySet.add(county);
      }
    });

    this.setState(
      {
        allOrders: list,
        counties: [
          "All",
          ...Array.from(countySet).sort(),
        ],
        isLoadingStats: false,
      },
      this.recomputeStats
    );
  } catch (error) {
    console.error("Failed to load orders:", error);
    Notify.error("Failed to calculate stats");

    this.setState({
      isLoadingStats: false,
    });
  }
};

  render() {
    const { stats, selectedCounty, counties } = this.state;

    return (
      <>
        <style>{`
          .overview-container { margin-top: 0; }
          @media (max-width: 576px) {
            .overview-container { margin-top: 70px; }
          }
        `}</style>

        <div className="container overview-container">
          {/* COUNTY FILTER DROPDOWN */}
          <div className="row wd-100 mb-4">
            <div className="col-12 d-flex justify-content-end">
              <div style={{ minWidth: 240 }}>
                <label className="mb-1 small text-muted">Filter by County</label>
                <select
                  className="form-control"
                  value={selectedCounty}
                  onChange={this.handleCountyChange}
                  style={{ color: "#73113b" }}
                >
                  {counties.map((c) => (
                    <option key={c} value={c}>
                      {c === "All" ? "All Counties" : c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* METRIC CARDS */}
          <div className="row wd-100">
            {stats && (
              <>
                <div className="col-lg-4 col-md-6 col-6 col-sm-6 mb-5">
                  <StatsCard
                    data={[
                      { statsInfo: `${stats.current_orders.total}`, title: "Current Orders", icon: ordersIcon },
                      { statsInfo: `${stats.current_orders.cork}`, title: "Cork" },
                      { statsInfo: `${stats.current_orders.limerick}`, title: "Limerick" },
                    ]}
                  />
                </div>

                <div className="col-lg-4 col-md-6 col-6 col-sm-6 mb-5">
                  <StatsCard
                    data={[
                      { statsInfo: `${stats.vendor_accepted.total}`, title: "Vendor Accepted", icon: vendorIcon },
                      { statsInfo: `${stats.vendor_accepted.cork}`, title: "Cork" },
                      { statsInfo: `${stats.vendor_accepted.limerick}`, title: "Limerick" },
                    ]}
                  />
                </div>

                <div className="col-lg-4 col-md-6 col-6 col-sm-6 mb-5">
                  <StatsCard
                    data={[
                      { statsInfo: `${stats.vendor_pending.total}`, title: "Vendor Pending", icon: vendorIconPending },
                      { statsInfo: `${stats.vendor_pending.cork}`, title: "Cork" },
                      { statsInfo: `${stats.vendor_pending.limerick}`, title: "Limerick" },
                    ]}
                  />
                </div>

                <div className="col-lg-4 col-md-6 col-6 col-sm-6 mb-5">
                  <StatsCard
                    data={[
                      { statsInfo: `${stats.driver_accepted.total}`, title: "Driver Accepted", icon: driverIcon },
                      { statsInfo: `${stats.driver_accepted.cork}`, title: "Cork" },
                      { statsInfo: `${stats.driver_accepted.limerick}`, title: "Limerick" },
                    ]}
                  />
                </div>

                <div className="col-lg-4 col-md-6 col-6 col-sm-6 mb-5">
                  <StatsCard
                    data={[
                      { statsInfo: `${stats.driver_pending.total}`, title: "Driver Pending", icon: driverIconPending },
                      { statsInfo: `${stats.driver_pending.cork}`, title: "Cork" },
                      { statsInfo: `${stats.driver_pending.limerick}`, title: "Limerick" },
                    ]}
                  />
                </div>

                <div className="col-lg-4 col-md-6 col-6 mb-5">
                  <StatsCard
                    data={[
                      { statsInfo: `${stats.rejected.total}`, title: "Rejected", icon: rejectedIcon },
                      { statsInfo: `${stats.rejected.cork}`, title: "Cork" },
                      { statsInfo: `${stats.rejected.limerick}`, title: "Limerick" },
                    ]}
                  />
                </div>
              </>
            )}
          </div>

          {/* ORDERS TABLE */}
          <OverviewTable
            // onOrdersLoaded={this.handleOrdersLoaded}
            selectedCounty={selectedCounty}
            extractCounty={extractCounty}
          />
        </div>
      </>
    );
  }
}
